import React from "react";
import { useLocation, matchPath } from "react-router-dom";
import SEO from "@/components/SEO";

/**
 * Per-route SEO metadata. Maps the React-Router pathname pattern to the
 * title + description that the SEO component will emit into <head> on
 * every navigation. Static social crawlers see the canonical metadata
 * from /public/index.html (the homepage card); Google + JS-aware
 * crawlers see the route-specific copy below.
 *
 * Patterns are matched top-to-bottom; the first match wins. Use
 * `/foo/:slug` style placeholders for dynamic segments.
 */
const PAGE_SEO = [
  {
    path: "/",
    title: null, // → "Grup Xaluca · The Soul of Southern Morocco"
    description:
      "Kasbahs, bivouacs and refuges across the Sahara, the Atlas and the great south of Morocco. Family-owned hospitality since 1995. Plan your enquiry — our concierge replies in 24h.",
  },
  {
    path: "/hotels",
    title: "Hotels & Kasbahs of Southern Morocco",
    description:
      "Discover Grup Xaluca's curated kasbahs and design hotels across Erfoud, Boumalne Dadès, Aït Ben Haddou and Midelt — each handcrafted, family-owned and rooted in the soul of southern Morocco.",
  },
  {
    path: "/hotels/:slug",
    title: "Hotel · Grup Xaluca",
    description:
      "A handcrafted Grup Xaluca address in southern Morocco — rooms, suites, gastronomy, spa and curated experiences. Plan your stay with our 24-hour concierge.",
  },
  {
    path: "/bivouacs",
    title: "Luxury Bivouacs in the Sahara",
    description:
      "Three desert camps in Erg Chebbi — La Belle Étoile, Sahara Stars and the Heritage tents. Hand-loomed kilims, candlelit dining and a night under 360° of stars.",
  },
  {
    path: "/booking",
    title: "Plan Your Stay · Booking Enquiry",
    description:
      "Build your itinerary across our hotels, bivouacs and refuges. Mix rooms, transfers, spa, gastronomy and excursions in a single enquiry. Our concierge replies within 24 hours.",
  },
  {
    path: "/experiences",
    title: "Experiences & Excursions",
    description:
      "4x4 desert tours, camel caravans, Atlas treks, Berber cookery, sunrise ballooning and bespoke itineraries — the signature experiences of southern Morocco, curated by Grup Xaluca.",
  },
  {
    path: "/trips",
    title: "Multi-Day Itineraries & Group Departures",
    description:
      "Multi-day journeys across the Atlas, the Sahara and the great south — designed by Grup Xaluca's concierges and ready to depart.",
  },
  {
    path: "/gastronomy",
    title: "Gastronomy · Moroccan Cooking & Wine",
    description:
      "The chefs, tagines, kemias and Moroccan wines of Grup Xaluca's tables across the south. From kasbah dining rooms to candlelit desert dinners.",
  },
  {
    path: "/events",
    title: "Weddings, Retreats & Private Events",
    description:
      "Host your celebration, wellness retreat or corporate offsite at a Grup Xaluca address — from kasbah courtyards to private Sahara camps for up to 200 guests.",
  },
  {
    path: "/about",
    title: "About · Family-Owned, Moroccan at Heart",
    description:
      "Founded in Erfoud in 1995, Grup Xaluca is a Moroccan family hospitality collection devoted to the great south. Learn our story, our team and our values.",
  },
  {
    path: "/contact",
    title: "Contact · Concierge & Reservations",
    description:
      "Reach the Grup Xaluca concierge — reservations, press, group travel and partnerships. We reply in your language within 24 hours.",
  },
  {
    path: "/press",
    title: "Press & Media Enquiries",
    description:
      "Press releases, brand assets, founder interviews and media enquiries for Grup Xaluca — the leading hotel collection of southern Morocco.",
  },
  {
    path: "/blog",
    title: "The Xaluca Journal · Stories From Southern Morocco",
    description:
      "Travel notes, desert journeys, recipes from the kasbah and field stories — the editorial journal of Grup Xaluca.",
  },
  {
    path: "/blog/:slug",
    title: "Journal · Grup Xaluca",
    description: "A story from the great south of Morocco — curated by Grup Xaluca.",
  },
  {
    path: "/pricing",
    title: "Public Rates · Hotels, Bivouacs & Refuges",
    description:
      "Indicative public rates for every Grup Xaluca address in MAD, EUR and USD. Final pricing confirmed by the concierge in MAD at booking.",
  },
  {
    path: "/southern-morocco",
    title: "Southern Morocco · The Land We Love",
    description:
      "Discover the Sahara, the Atlas, the great kasbah road and the oases of southern Morocco — through the eyes of a family that has lived here for four generations.",
  },
  {
    path: "/work-with-us",
    title: "Work With Us · Careers at Grup Xaluca",
    description:
      "Join the Grup Xaluca family. Open roles across our hotels, bivouacs and concierge teams in Erfoud, Merzouga, Boumalne Dadès and beyond.",
  },
  {
    path: "/wall-of-love",
    title: "Wall of Love · Guest Letters",
    description:
      "Five-star letters, photos and recommendations from the guests of Grup Xaluca — Sahara honeymooners, four-generation families, photographers, journalists and more.",
  },
];

const noindexPatterns = ["/admin", "/admin/*"];

const RouteSEO = () => {
  const { pathname } = useLocation();

  if (noindexPatterns.some((p) => matchPath({ path: p, end: false }, pathname))) {
    return <SEO title="Admin" description="" path={pathname} noindex />;
  }

  const entry =
    PAGE_SEO.find((e) => matchPath({ path: e.path, end: true }, pathname)) ||
    PAGE_SEO[0];

  return <SEO title={entry.title} description={entry.description} path={pathname} />;
};

export default RouteSEO;
